/**
 * =========================================================
 * Social Media Hub - Premium Dashboard Layout
 * =========================================================
 *
 * Modern, sleek dashboard layout with premium visual design,
 * smooth transitions, and polished user experience.
 */

import { useEffect } from "react";

// react-router-dom components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React context
import { useMaterialUIController, setLayout } from "context";

function DashboardLayout({ children }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const { pathname } = useLocation();

  useEffect(() => {
    setLayout(dispatch, "dashboard");
  }, [pathname]);

  return (
    <MDBox
      sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
        p: { xs: 2, sm: 2.5, md: 3 },
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        // iOS safe area support for notch and home indicator
        paddingTop: {
          xs: "calc(env(safe-area-inset-top) + 16px)",
          sm: "calc(env(safe-area-inset-top) + 20px)",
          md: 3,
        },
        paddingBottom: {
          xs: "calc(env(safe-area-inset-bottom) + 16px)",
          sm: "calc(env(safe-area-inset-bottom) + 20px)",
          md: 3,
        },
        paddingLeft: {
          xs: "calc(env(safe-area-inset-left) + 16px)",
          sm: "calc(env(safe-area-inset-left) + 20px)",
          md: 3,
        },
        paddingRight: {
          xs: "calc(env(safe-area-inset-right) + 16px)",
          sm: "calc(env(safe-area-inset-right) + 20px)",
          md: 3,
        },
        // Premium background with subtle gradient
        background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
        backgroundAttachment: "fixed",

        [breakpoints.up("xl")]: {
          marginLeft: miniSidenav ? pxToRem(96) : pxToRem(294),
          transition: transitions.create(["margin-left", "margin-right"], {
            easing: transitions.easing.easeInOut,
            duration: transitions.duration.standard,
          }),
        },

        // Smooth scroll behavior
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#cbd5e1",
          borderRadius: "10px",
          "&:hover": {
            background: "#94a3b8",
          },
        },
      })}
    >
      {children}
    </MDBox>
  );
}

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
